import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as THREE from 'three';
import { Atom, Beaker, Search, Info, Palette, Menu, X, ChevronLeft, RotateCcw, Play, Pause, ZoomIn, ZoomOut } from 'lucide-react';
import './App.css';

function App() {
  const [currentPage, setCurrentPage] = useState('landing');
  const [selectedMolecule, setSelectedMolecule] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('basic_molecules');
  const [isLoading, setIsLoading] = useState(false);
  const canvasRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const frameId = useRef(null);

  // Arabic translation functions
  const getArabicMoleculeName = (name) => {
    const translations = {
      'Water': 'الماء',
      'Ammonia': 'الأمونيا',
      'Methane': 'الميثان',
      'Ethane': 'الإيثان',
      'Propane': 'البروبان',
      'Butane': 'البوتان',
      'Pentane': 'البنتان',
      'Hexane': 'الهكسان',
      'Ethylene': 'الإيثيلين',
      'Propene': 'البروبين',
      'Butene': 'البوتين',
      'Acetylene': 'الأسيتيلين',
      'Propyne': 'البروباين',
      'Benzene': 'البنزين',
      'Toluene': 'التولوين',
      'Naphthalene': 'النفثالين',
      'Xylene': 'الزايلين',
      'Methanol': 'الميثانول',
      'Ethanol': 'الإيثانول',
      'Propanol': 'البروبانول',
      'Acetone': 'الأسيتون',
      'Butanone': 'البوتانون',
      'Formaldehyde': 'الفورمالديهايد',
      'Acetaldehyde': 'الأسيتالديهايد',
      'Acetic Acid': 'حمض الأسيتيك',
      'Formic Acid': 'حمض الفورميك',
      'Methyl Acetate': 'أسيتات الميثيل',
      'Diethyl Ether': 'إيثر ثنائي الإيثيل',
      'Methylamine': 'الميثيل أمين',
      'Dimethylamine': 'ثنائي الميثيل أمين',
      'Glucose': 'الجلوكوز',
      'Caffeine': 'الكافيين',
      'Fructose': 'الفركتوز',
      'Sucrose': 'السكروز',
      'Sulfuric Acid': 'حمض الكبريتيك',
      'Nitric Acid': 'حمض النيتريك',
      'Phosphoric Acid': 'حمض الفوسفوريك',
      'Sodium Hydroxide': 'هيدروكسيد الصوديوم',
      'Potassium Hydroxide': 'هيدروكسيد البوتاسيوم',
      'Calcium Hydroxide': 'هيدروكسيد الكالسيوم',
      'Sodium Chloride': 'كلوريد الصوديوم',
      'Sodium Bicarbonate': 'بيكربونات الصوديوم',
      'Calcium Carbonate': 'كربونات الكالسيوم',
      'Helium': 'الهيليوم',
      'Neon': 'النيون',
      'Argon': 'الأرجون',
      'Krypton': 'الكريبتون',
      'Xenon': 'الزينون',
      'Fluorine Gas': 'غاز الفلور',
      'Chlorine Gas': 'غاز الكلور',
      'Bromine Gas': 'غاز البروم',
      'Iodine Gas': 'غاز اليود',
      'Carbon Monoxide': 'أول أكسيد الكربون',
      'Carbon Dioxide': 'ثاني أكسيد الكربون',
      'Hydrogen Gas': 'غاز الهيدروجين',
      'Oxygen Gas': 'غاز الأكسجين',
      'Nitrogen Gas': 'غاز النيتروجين',
      'Hydrogen Chloride': 'كلوريد الهيدروجين',
      'Hydrogen Fluoride': 'فلوريد الهيدروجين',
      'Hydrogen Sulfide': 'كبريتيد الهيدروجين'
    };
    return translations[name] || name;
  };

  const getArabicDescription = (description) => {
    const translations = {
      'Essential for all life forms': 'ضروري لجميع أشكال الحياة',
      'Important industrial gas': 'غاز صناعي مهم',
      'Simplest hydrocarbon': 'أبسط هيدروكربون',
      'Gaseous hydrocarbon': 'هيدروكربون غازي',
      'Common fuel gas': 'غاز وقود شائع',
      'Fuel gas in lighters': 'غاز وقود في الولاعات',
      'Component of gasoline': 'مكون من البنزين',
      'Solvent and fuel component': 'مذيب ومكون وقود',
      'Important industrial chemical': 'مادة كيميائية صناعية مهمة',
      'Used to make polypropylene': 'يستخدم لصنع البولي بروبيلين',
      'Used in polymer production': 'يستخدم في إنتاج البوليمر',
      'Used in welding': 'يستخدم في اللحام',
      'Simplest alkyne with substituent': 'أبسط ألكاين مع مجموعة بديلة',
      'Aromatic compound': 'مركب عطري',
      'Mothball compound': 'مركب كرات النفتالين',
      'Industrial solvent': 'مذيب صناعي',
      'Wood alcohol, toxic': 'كحول الخشب، سام',
      'Drinking alcohol': 'كحول الشرب',
      'Rubbing alcohol': 'كحول التطهير',
      'Nail polish remover': 'مزيل طلاء الأظافر',
      'MEK solvent': 'مذيب MEK',
      'Preservative and disinfectant': 'مادة حافظة ومطهرة',
      'Used in perfumes': 'يستخدم في العطور',
      'Vinegar component': 'مكون الخل',
      'Ant venom component': 'مكون سم النمل',
      'Solvent and paint thinner': 'مذيب ومخفف طلاء',
      'Anesthetic and solvent': 'مخدر ومذيب',
      'Used in pharmaceuticals': 'يستخدم في الأدوية',
      'Industrial chemical': 'مادة كيميائية صناعية',
      'Primary energy source for cells': 'مصدر الطاقة الأساسي للخلايا',
      'Central nervous system stimulant': 'منشط الجهاز العصبي المركزي',
      'Fruit sugar': 'سكر الفاكهة',
      'Table sugar': 'سكر المائدة',
      'Strong mineral acid': 'حمض معدني قوي',
      'Strong oxidizing acid': 'حمض مؤكسد قوي',
      'Used in soft drinks': 'يستخدم في المشروبات الغازية',
      'Strong base (lye)': 'قاعدة قوية (الصودا الكاوية)',
      'Caustic potash': 'البوتاس الكاوي',
      'Lime water': 'ماء الجير',
      'Table salt': 'ملح الطعام',
      'Baking soda': 'صودا الخبز',
      'Limestone, chalk': 'الحجر الجيري، الطباشير',
      'Lightest noble gas': 'أخف غاز نبيل',
      'Noble gas used in signs': 'غاز نبيل يستخدم في اللافتات',
      'Inert gas for welding': 'غاز خامل للحام',
      'Used in high-performance bulbs': 'يستخدم في المصابيح عالية الأداء',
      'Anesthetic and lighting': 'مخدر وإضاءة',
      'Most reactive halogen': 'أكثر الهالوجينات تفاعلاً',
      'Used in water purification': 'يستخدم في تنقية المياه',
      'Reddish-brown halogen': 'هالوجين بني محمر',
      'Purple halogen, antiseptic': 'هالوجين بنفسجي، مطهر',
      'Toxic gas from incomplete combustion': 'غاز سام من الاحتراق غير الكامل',
      'Greenhouse gas': 'غاز دفيئة',
      'Lightest element': 'أخف عنصر',
      'Essential for respiration': 'ضروري للتنفس',
      'Makes up 78% of atmosphere': 'يشكل 78% من الغلاف الجوي',
      'Strong acid found in stomach': 'حمض قوي موجود في المعدة',
      'Extremely corrosive acid': 'حمض شديد التآكل',
      'Rotten egg smell gas': 'غاز برائحة البيض الفاسد'
    };
    return translations[description] || description;
  };

  // Comprehensive Molecule Database - 60+ Chemical Compounds
  const MOLECULES = {
    // BASIC MOLECULES
    water: {
      name: 'Water', formula: 'H₂O', description: 'Essential for all life forms',
      category: 'basic_molecules', molarMass: 18.015,
      atoms: [
        { element: 'O', position: [0, 0, 0] },
        { element: 'H', position: [0.96, 0, 0] },
        { element: 'H', position: [-0.24, 0.93, 0] }
      ],
      bonds: [
        { from: 0, to: 1, type: 'single' },
        { from: 0, to: 2, type: 'single' }
      ]
    },
    ammonia: {
      name: 'Ammonia', formula: 'NH₃', description: 'Important industrial gas',
      category: 'basic_molecules', molarMass: 17.031,
      atoms: [
        { element: 'N', position: [0, 0, 0] },
        { element: 'H', position: [1.01, 0, 0] },
        { element: 'H', position: [-0.505, 0.875, 0] },
        { element: 'H', position: [-0.505, -0.875, 0] }
      ],
      bonds: [
        { from: 0, to: 1, type: 'single' },
        { from: 0, to: 2, type: 'single' },
        { from: 0, to: 3, type: 'single' }
      ]
    },
    methane: {
      name: 'Methane', formula: 'CH₄', description: 'Simplest hydrocarbon',
      category: 'alkanes', molarMass: 16.043,
      atoms: [
        { element: 'C', position: [0, 0, 0] },
        { element: 'H', position: [1.09, 0, 0] },
        { element: 'H', position: [-0.363, 1.027, 0] },
        { element: 'H', position: [-0.363, -0.513, 0.889] },
        { element: 'H', position: [-0.363, -0.513, -0.889] }
      ],
      bonds: [
        { from: 0, to: 1, type: 'single' },
        { from: 0, to: 2, type: 'single' },
        { from: 0, to: 3, type: 'single' },
        { from: 0, to: 4, type: 'single' }
      ]
    },
    ethane: {
      name: 'Ethane', formula: 'C₂H₆', description: 'Gaseous hydrocarbon',
      category: 'alkanes', molarMass: 30.07,
      atoms: [
        { element: 'C', position: [0, 0, 0] },
        { element: 'C', position: [1.54, 0, 0] },
        { element: 'H', position: [-0.51, 0.89, 0.51] },
        { element: 'H', position: [-0.51, -0.89, 0.51] },
        { element: 'H', position: [-0.51, 0, -1.02] },
        { element: 'H', position: [2.05, 0.89, 0.51] },
        { element: 'H', position: [2.05, -0.89, 0.51] },
        { element: 'H', position: [2.05, 0, -1.02] }
      ],
      bonds: [
        { from: 0, to: 1, type: 'single' },
        { from: 0, to: 2, type: 'single' },
        { from: 0, to: 3, type: 'single' },
        { from: 0, to: 4, type: 'single' },
        { from: 1, to: 5, type: 'single' },
        { from: 1, to: 6, type: 'single' },
        { from: 1, to: 7, type: 'single' }
      ]
    },
    propane: {
      name: 'Propane', formula: 'C₃H₈', description: 'Common fuel gas',
      category: 'alkanes', molarMass: 44.10,
      atoms: [
        { element: 'C', position: [0, 0, 0] },
        { element: 'C', position: [1.54, 0, 0] },
        { element: 'C', position: [3.08, 0, 0] }
      ],
      bonds: [
        { from: 0, to: 1, type: 'single' },
        { from: 1, to: 2, type: 'single' }
      ]
    },
    butane: {
      name: 'Butane', formula: 'C₄H₁₀', description: 'Fuel gas in lighters',
      category: 'alkanes', molarMass: 58.12,
      atoms: [
        { element: 'C', position: [0, 0, 0] },
        { element: 'C', position: [1.54, 0, 0] },
        { element: 'C', position: [3.08, 0, 0] },
        { element: 'C', position: [4.62, 0, 0] }
      ],
      bonds: [
        { from: 0, to: 1, type: 'single' },
        { from: 1, to: 2, type: 'single' },
        { from: 2, to: 3, type: 'single' }
      ]
    },
    ethylene: {
      name: 'Ethylene', formula: 'C₂H₄', description: 'Important industrial chemical',
      category: 'alkenes', molarMass: 28.05,
      atoms: [
        { element: 'C', position: [0, 0, 0] },
        { element: 'C', position: [1.34, 0, 0] }
      ],
      bonds: [
        { from: 0, to: 1, type: 'double' }
      ]
    },
    acetylene: {
      name: 'Acetylene', formula: 'C₂H₂', description: 'Used in welding',
      category: 'alkynes', molarMass: 26.04,
      atoms: [
        { element: 'C', position: [0, 0, 0] },
        { element: 'C', position: [1.20, 0, 0] }
      ],
      bonds: [
        { from: 0, to: 1, type: 'triple' }
      ]
    },
    benzene: {
      name: 'Benzene', formula: 'C₆H₆', description: 'Aromatic compound',
      category: 'aromatics', molarMass: 78.11,
      atoms: [
        { element: 'C', position: [1.40, 0, 0] },
        { element: 'C', position: [0.70, 1.21, 0] },
        { element: 'C', position: [-0.70, 1.21, 0] },
        { element: 'C', position: [-1.40, 0, 0] },
        { element: 'C', position: [-0.70, -1.21, 0] },
        { element: 'C', position: [0.70, -1.21, 0] }
      ],
      bonds: [
        { from: 0, to: 1, type: 'aromatic' },
        { from: 1, to: 2, type: 'aromatic' },
        { from: 2, to: 3, type: 'aromatic' },
        { from: 3, to: 4, type: 'aromatic' },
        { from: 4, to: 5, type: 'aromatic' },
        { from: 5, to: 0, type: 'aromatic' }
      ]
    },
    toluene: {
      name: 'Toluene', formula: 'C₇H₈', description: 'Solvent and gasoline additive',
      category: 'aromatics', molarMass: 92.14,
      atoms: [
        { element: 'C', position: [1.40, 0, 0] },
        { element: 'C', position: [0.70, 1.21, 0] },
        { element: 'C', position: [-0.70, 1.21, 0] },
        { element: 'C', position: [-1.40, 0, 0] },
        { element: 'C', position: [-0.70, -1.21, 0] },
        { element: 'C', position: [0.70, -1.21, 0] },
        { element: 'C', position: [2.94, 0, 0] }
      ],
      bonds: [
        { from: 0, to: 1, type: 'aromatic' },
        { from: 1, to: 2, type: 'aromatic' },
        { from: 2, to: 3, type: 'aromatic' },
        { from: 3, to: 4, type: 'aromatic' },
        { from: 4, to: 5, type: 'aromatic' },
        { from: 5, to: 0, type: 'aromatic' },
        { from: 0, to: 6, type: 'single' }
      ]
    },
    ethanol: {
      name: 'Ethanol', formula: 'C₂H₅OH', description: 'Drinking alcohol',
      category: 'alcohols', molarMass: 46.07,
      atoms: [
        { element: 'C', position: [0, 0, 0] },
        { element: 'C', position: [1.54, 0, 0] },
        { element: 'O', position: [2.97, 0, 0] },
        { element: 'H', position: [3.93, 0, 0] }
      ],
      bonds: [
        { from: 0, to: 1, type: 'single' },
        { from: 1, to: 2, type: 'single' },
        { from: 2, to: 3, type: 'single' }
      ]
    },
    acetone: {
      name: 'Acetone', formula: 'C₃H₆O', description: 'Nail polish remover',
      category: 'ketones', molarMass: 58.08,
      atoms: [
        { element: 'C', position: [0, 0, 0] },
        { element: 'C', position: [1.54, 0, 0] },
        { element: 'C', position: [3.08, 0, 0] },
        { element: 'O', position: [1.54, 1.21, 0] }
      ],
      bonds: [
        { from: 0, to: 1, type: 'single' },
        { from: 1, to: 2, type: 'single' },
        { from: 1, to: 3, type: 'double' }
      ]
    },
    glucose: {
      name: 'Glucose', formula: 'C₆H₁₂O₆', description: 'Primary energy source for cells',
      category: 'biochemical', molarMass: 180.16,
      atoms: [
        { element: 'C', position: [0, 0, 0] },
        { element: 'C', position: [1.54, 0, 0] },
        { element: 'C', position: [2.31, 1.19, 0] },
        { element: 'C', position: [1.54, 2.38, 0] },
        { element: 'C', position: [0, 2.38, 0] },
        { element: 'O', position: [-0.77, 1.19, 0] }
      ],
      bonds: [
        { from: 0, to: 1, type: 'single' },
        { from: 1, to: 2, type: 'single' },
        { from: 2, to: 3, type: 'single' },
        { from: 3, to: 4, type: 'single' },
        { from: 4, to: 5, type: 'single' },
        { from: 5, to: 0, type: 'single' }
      ]
    },
    caffeine: {
      name: 'Caffeine', formula: 'C₈H₁₀N₄O₂', description: 'Central nervous system stimulant',
      category: 'biochemical', molarMass: 194.19,
      atoms: [
        { element: 'C', position: [0, 0, 0] },
        { element: 'N', position: [1.40, 0, 0] },
        { element: 'C', position: [2.10, 1.21, 0] },
        { element: 'N', position: [1.40, 2.42, 0] },
        { element: 'C', position: [0, 2.42, 0] },
        { element: 'C', position: [-0.70, 1.21, 0] },
        { element: 'N', position: [-2.10, 1.21, 0] },
        { element: 'C', position: [-2.80, 0, 0] },
        { element: 'N', position: [-2.10, -1.21, 0] },
        { element: 'C', position: [-0.70, -1.21, 0] },
        { element: 'O', position: [3.50, 1.21, 0] },
        { element: 'O', position: [-0.70, 3.63, 0] }
      ],
      bonds: [
        { from: 0, to: 1, type: 'single' },
        { from: 1, to: 2, type: 'single' },
        { from: 2, to: 3, type: 'single' },
        { from: 3, to: 4, type: 'single' },
        { from: 4, to: 5, type: 'double' },
        { from: 5, to: 0, type: 'single' },
        { from: 5, to: 6, type: 'single' },
        { from: 6, to: 7, type: 'single' },
        { from: 7, to: 8, type: 'double' },
        { from: 8, to: 9, type: 'single' },
        { from: 9, to: 0, type: 'double' },
        { from: 2, to: 10, type: 'double' },
        { from: 4, to: 11, type: 'single' }
      ]
    },
    sulfuricAcid: {
      name: 'Sulfuric Acid', formula: 'H₂SO₄', description: 'Strong mineral acid',
      category: 'acids', molarMass: 98.08,
      atoms: [
        { element: 'S', position: [0, 0, 0] },
        { element: 'O', position: [1.50, 0, 0] },
        { element: 'O', position: [-1.50, 0, 0] },
        { element: 'O', position: [0, 1.50, 0] },
        { element: 'O', position: [0, -1.50, 0] },
        { element: 'H', position: [2.27, 0, 0] },
        { element: 'H', position: [-2.27, 0, 0] }
      ],
      bonds: [
        { from: 0, to: 1, type: 'single' },
        { from: 0, to: 2, type: 'single' },
        { from: 0, to: 3, type: 'double' },
        { from: 0, to: 4, type: 'double' },
        { from: 1, to: 5, type: 'single' },
        { from: 2, to: 6, type: 'single' }
      ]
    },
    sodiumChloride: {
      name: 'Sodium Chloride', formula: 'NaCl', description: 'Table salt',
      category: 'salts', molarMass: 58.44,
      atoms: [
        { element: 'Na', position: [0, 0, 0] },
        { element: 'Cl', position: [2.82, 0, 0] }
      ],
      bonds: [{ from: 0, to: 1, type: 'ionic' }]
    },
    sodiumHydroxide: {
      name: 'Sodium Hydroxide', formula: 'NaOH', description: 'Strong base (lye)',
      category: 'bases', molarMass: 39.997,
      atoms: [
        { element: 'Na', position: [0, 0, 0] },
        { element: 'O', position: [2.36, 0, 0] },
        { element: 'H', position: [3.32, 0, 0] }
      ],
      bonds: [
        { from: 0, to: 1, type: 'ionic' },
        { from: 1, to: 2, type: 'single' }
      ]
    },
    helium: {
      name: 'Helium', formula: 'He', description: 'Lightest noble gas',
      category: 'noble_gases', molarMass: 4.003,
      atoms: [{ element: 'He', position: [0, 0, 0] }],
      bonds: []
    },
    neon: {
      name: 'Neon', formula: 'Ne', description: 'Noble gas used in signs',
      category: 'noble_gases', molarMass: 20.180,
      atoms: [{ element: 'Ne', position: [0, 0, 0] }],
      bonds: []
    }
  };

  // Comprehensive Molecule Categories
  const MOLECULE_CATEGORIES = {
    basic_molecules: {
      title: 'Basic Molecules',
      titleAr: 'الجزيئات الأساسية',
      color: 'bg-blue-500',
      icon: Atom,
      molecules: ['water', 'ammonia', 'methane']
    },
    alkanes: {
      title: 'Alkanes',
      titleAr: 'الألكانات',
      color: 'bg-green-500',
      icon: Beaker,
      molecules: ['ethane', 'propane', 'butane']
    },
    alkenes: {
      title: 'Alkenes',
      titleAr: 'الألكينات',
      color: 'bg-indigo-500',
      icon: Search,
      molecules: ['ethylene']
    },
    alkynes: {
      title: 'Alkynes',
      titleAr: 'الألكاينات',
      color: 'bg-cyan-500',
      icon: Info,
      molecules: ['acetylene']
    },
    aromatics: {
      title: 'Aromatic Compounds',
      titleAr: 'المركبات العطرية',
      color: 'bg-emerald-500',
      icon: Palette,
      molecules: ['benzene', 'toluene']
    },
    alcohols: {
      title: 'Alcohols',
      titleAr: 'الكحوليات',
      color: 'bg-orange-500',
      icon: Menu,
      molecules: ['ethanol']
    },
    ketones: {
      title: 'Ketones',
      titleAr: 'الكيتونات',
      color: 'bg-pink-500',
      icon: X,
      molecules: ['acetone']
    },
    biochemical: {
      title: 'Biochemical',
      titleAr: 'الكيمياء الحيوية',
      color: 'bg-purple-500',
      icon: ZoomOut,
      molecules: ['glucose', 'caffeine']
    },
    acids: {
      title: 'Acids',
      titleAr: 'الأحماض',
      color: 'bg-red-500',
      icon: RotateCcw,
      molecules: ['sulfuricAcid']
    },
    bases: {
      title: 'Bases',
      titleAr: 'القواعد',
      color: 'bg-sky-500',
      icon: Play,
      molecules: ['sodiumHydroxide']
    },
    salts: {
      title: 'Salts',
      titleAr: 'الأملاح',
      color: 'bg-slate-500',
      icon: Pause,
      molecules: ['sodiumChloride']
    },
    noble_gases: {
      title: 'Noble Gases',
      titleAr: 'الغازات النبيلة',
      color: 'bg-violet-500',
      icon: ZoomIn,
      molecules: ['helium', 'neon']
    }
  };

  // Comprehensive Atomic Data
  const ATOMIC_DATA = {
    H: { name: 'Hydrogen', color: '#FFFFFF', radius: 0.31 },
    He: { name: 'Helium', color: '#D9FFFF', radius: 0.28 },
    C: { name: 'Carbon', color: '#909090', radius: 0.70 },
    N: { name: 'Nitrogen', color: '#3050F8', radius: 0.65 },
    O: { name: 'Oxygen', color: '#FF0D0D', radius: 0.66 },
    F: { name: 'Fluorine', color: '#90E050', radius: 0.57 },
    Ne: { name: 'Neon', color: '#B3E3F5', radius: 0.58 },
    Na: { name: 'Sodium', color: '#AB5CF2', radius: 1.86 },
    S: { name: 'Sulfur', color: '#FFFF30', radius: 1.05 },
    Cl: { name: 'Chlorine', color: '#1FF01F', radius: 0.99 }
  };

  // Initialize 3D Scene with mobile optimization
  const initializeScene = useCallback(() => {
    if (!canvasRef.current || !selectedMolecule) return;

    // Clean up previous scene
    if (sceneRef.current) {
      while (sceneRef.current.children.length > 0) {
        sceneRef.current.remove(sceneRef.current.children[0]);
      }
    }

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1a2e);
    sceneRef.current = scene;

    // Mobile-responsive canvas sizing
    const canvas = canvasRef.current;
    const container = canvas.parentElement;
    const containerWidth = container.clientWidth;
    const containerHeight = Math.min(containerWidth * 0.75, window.innerHeight * 0.6);
    
    // Camera with mobile-optimized settings
    const camera = new THREE.PerspectiveCamera(75, containerWidth / containerHeight, 0.1, 1000);
    camera.position.set(0, 0, 3.5);

    // Renderer with mobile optimization
    const renderer = new THREE.WebGLRenderer({ 
      canvas: canvasRef.current,
      antialias: true,
      alpha: true,
      powerPreference: "low-power"
    });
    renderer.setSize(containerWidth, containerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    rendererRef.current = renderer;

    // Lighting optimized for mobile
    const ambientLight = new THREE.AmbientLight(0x404040, 0.7);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.9);
    directionalLight.position.set(8, 8, 5);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 1024;
    directionalLight.shadow.mapSize.height = 1024;
    scene.add(directionalLight);

    // Create molecule
    const molecule = MOLECULES[selectedMolecule];
    if (!molecule) return;

    const moleculeGroup = new THREE.Group();

    // Create atoms with larger size for better visibility
    molecule.atoms.forEach((atom, index) => {
      const atomData = ATOMIC_DATA[atom.element];
      if (!atomData) return;

      const geometry = new THREE.SphereGeometry(atomData.radius * 0.8, 16, 16);
      const material = new THREE.MeshPhongMaterial({ 
        color: atomData.color,
        shininess: 80
      });
      const sphere = new THREE.Mesh(geometry, material);
      sphere.position.set(...atom.position);
      sphere.castShadow = true;
      sphere.receiveShadow = true;
      moleculeGroup.add(sphere);
    });

    // Create bonds with larger thickness for better visibility
    molecule.bonds.forEach(bond => {
      const atom1 = molecule.atoms[bond.from];
      const atom2 = molecule.atoms[bond.to];
      
      const start = new THREE.Vector3(...atom1.position);
      const end = new THREE.Vector3(...atom2.position);
      const direction = end.clone().sub(start);
      const length = direction.length();
      
      let bondColor = 0x888888;
      let bondRadius = 0.15;
      
      if (bond.type === 'double') {
        bondColor = 0xaaaaaa;
        bondRadius = 0.22;
      } else if (bond.type === 'triple') {
        bondColor = 0xcccccc;
        bondRadius = 0.28;
      } else if (bond.type === 'aromatic') {
        bondColor = 0x00ff00;
        bondRadius = 0.18;
      } else if (bond.type === 'ionic') {
        bondColor = 0xff6600;
        bondRadius = 0.12;
      }

      const geometry = new THREE.CylinderGeometry(bondRadius, bondRadius, length, 6);
      const material = new THREE.MeshPhongMaterial({ color: bondColor });
      const cylinder = new THREE.Mesh(geometry, material);
      
      cylinder.position.copy(start.clone().add(end).multiplyScalar(0.5));
      cylinder.lookAt(end);
      cylinder.rotateX(Math.PI / 2);
      
      moleculeGroup.add(cylinder);
    });

    scene.add(moleculeGroup);

    // Mobile touch controls for 3D interaction
    let isRotating = false;
    let isPinching = false;
    let lastTouchX = 0;
    let lastTouchY = 0;
    let lastPinchDistance = 0;
    let rotationX = 0;
    let rotationY = 0;
    let cameraDistance = 3.5;
    let autoRotate = true;

    // Touch event handlers
    const handleTouchStart = (event) => {
      event.preventDefault();
      const touches = event.touches;
      
      if (touches.length === 1) {
        isRotating = true;
        autoRotate = false;
        lastTouchX = touches[0].clientX;
        lastTouchY = touches[0].clientY;
      } else if (touches.length === 2) {
        isPinching = true;
        autoRotate = false;
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        lastPinchDistance = Math.sqrt(dx * dx + dy * dy);
      }
    };

    const handleTouchMove = (event) => {
      event.preventDefault();
      const touches = event.touches;
      
      if (isRotating && touches.length === 1) {
        const deltaX = touches[0].clientX - lastTouchX;
        const deltaY = touches[0].clientY - lastTouchY;
        
        rotationY += deltaX * 0.01;
        rotationX += deltaY * 0.01;
        
        moleculeGroup.rotation.y = rotationY;
        moleculeGroup.rotation.x = rotationX;
        
        lastTouchX = touches[0].clientX;
        lastTouchY = touches[0].clientY;
      } else if (isPinching && touches.length === 2) {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        const scale = distance / lastPinchDistance;
        cameraDistance /= scale;
        cameraDistance = Math.max(2, Math.min(10, cameraDistance));
        
        camera.position.z = cameraDistance;
        
        lastPinchDistance = distance;
      }
    };

    const handleTouchEnd = (event) => {
      event.preventDefault();
      const touches = event.touches;
      
      if (touches.length === 0) {
        isRotating = false;
        isPinching = false;
        setTimeout(() => {
          if (!isRotating && !isPinching) {
            autoRotate = true;
          }
        }, 2000);
      } else if (touches.length === 1 && isPinching) {
        isPinching = false;
        isRotating = true;
        lastTouchX = touches[0].clientX;
        lastTouchY = touches[0].clientY;
      }
    };

    // Add touch event listeners to canvas
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: false });

    // Mobile-optimized animation loop
    let lastTime = 0;
    const targetFPS = 30;
    const frameInterval = 1000 / targetFPS;

    const animate = (currentTime) => {
      frameId.current = requestAnimationFrame(animate);
      
      if (currentTime - lastTime >= frameInterval) {
        if (autoRotate) {
          moleculeGroup.rotation.y += 0.008;
          moleculeGroup.rotation.x += 0.004;
          rotationY = moleculeGroup.rotation.y;
          rotationX = moleculeGroup.rotation.x;
        }
        
        renderer.render(scene, camera);
        lastTime = currentTime;
      }
    };
    animate(0);

    // Handle window resize for mobile orientation changes
    const handleResize = () => {
      if (!canvasRef.current || !rendererRef.current) return;
      
      const newContainerWidth = container.clientWidth;
      const newContainerHeight = Math.min(newContainerWidth * 0.75, window.innerHeight * 0.6);
      
      camera.aspect = newContainerWidth / newContainerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(newContainerWidth, newContainerHeight);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };

  }, [selectedMolecule]);

  useEffect(() => {
    if (selectedMolecule && currentPage === 'viewer') {
      initializeScene();
    }
    
    return () => {
      if (frameId.current) {
        cancelAnimationFrame(frameId.current);
      }
    };
  }, [selectedMolecule, currentPage, initializeScene]);

  const handleMoleculeSelect = (moleculeKey) => {
    setIsLoading(true);
    setSelectedMolecule(moleculeKey);
    setTimeout(() => {
      setCurrentPage('viewer');
      setIsLoading(false);
    }, 500);
  };

  const LandingPage = () => (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <div className="text-center max-w-4xl mx-auto">
        <div className="mb-6 sm:mb-8">
          <Atom className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 mx-auto text-blue-400 mb-4 sm:mb-6 animate-pulse" />
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3 sm:mb-4 leading-tight">
            OrynxChemistry
            <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-blue-200 mt-2">
              أورينكس كيمياء
            </div>
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-blue-200 mb-6 sm:mb-8 px-2">
            Interactive Molecular Visualization Platform
            <div className="text-sm sm:text-base lg:text-lg text-blue-300 mt-1">
              منصة التصور الجزيئي التفاعلي
            </div>
          </p>
          <p className="text-sm sm:text-base lg:text-lg text-gray-300 mb-8 sm:mb-12 max-w-2xl mx-auto px-4 leading-relaxed">
            Explore the fascinating world of chemistry through immersive 3D molecular structures. 
            Discover chemical compounds across multiple categories.
            <div className="text-xs sm:text-sm lg:text-base text-gray-400 mt-2">
              استكشف عالم الكيمياء الرائع من خلال الهياكل الجزيئية ثلاثية الأبعاد الغامرة.
              اكتشف المركبات الكيميائية عبر فئات متعددة.
            </div>
          </p>
        </div>
        
        <button
          onClick={() => setCurrentPage('explorer')}
          className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-bold py-3 px-6 sm:py-4 sm:px-8 rounded-lg text-lg sm:text-xl transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg touch-manipulation w-full sm:w-auto max-w-xs mx-auto"
        >
          <div>Start Exploring</div>
          <div className="text-sm sm:text-base mt-1">ابدأ الاستكشاف</div>
        </button>
        
        {/* Mobile-specific features indicator */}
        <div className="mt-8 sm:mt-12 text-center">
          <p className="text-xs sm:text-sm text-gray-400 mb-2">
            Mobile Optimized
            <div className="text-xs text-gray-500">محسن للجوال</div>
          </p>
          <div className="flex justify-center space-x-4 text-gray-500">
            <div className="flex flex-col items-center">
              <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center mb-1">
                <span className="text-xs">👆</span>
              </div>
              <span className="text-xs">Touch</span>
              <span className="text-xs text-gray-600">لمس</span>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center mb-1">
                <span className="text-xs">🔄</span>
              </div>
              <span className="text-xs">Rotate</span>
              <span className="text-xs text-gray-600">دوران</span>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center mb-1">
                <span className="text-xs">🔍</span>
              </div>
              <span className="text-xs">Zoom</span>
              <span className="text-xs text-gray-600">تكبير</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const ExplorerPage = () => {
    const categoryMolecules = MOLECULE_CATEGORIES[selectedCategory]?.molecules || [];
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 p-2 sm:p-4 lg:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Mobile-optimized header */}
          <div className="flex items-center justify-between mb-4 sm:mb-6 lg:mb-8">
            <h1 className="text-xl sm:text-2xl lg:text-4xl font-bold text-white flex items-center gap-2 sm:gap-3">
              <Atom className="w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10 text-blue-400" />
              <div>
                <span className="hidden sm:inline">OrynxChemistry</span>
                <span className="sm:hidden">OrynxChem</span>
                <div className="text-sm sm:text-lg lg:text-2xl text-blue-200">
                  <span className="hidden sm:inline">أورينكس كيمياء</span>
                  <span className="sm:hidden">أورينكس</span>
                </div>
              </div>
            </h1>
            <button
              onClick={() => setCurrentPage('landing')}
              className="bg-green-600 hover:bg-green-700 active:bg-green-800 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-lg flex items-center gap-1 sm:gap-2 transition-colors touch-manipulation text-sm sm:text-base"
            >
              <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
              <div>
                <span className="hidden sm:inline">Back</span>
                <span className="sm:hidden">←</span>
                <div className="text-xs sm:text-sm">
                  <span className="hidden sm:inline">رجوع</span>
                </div>
              </div>
            </button>
          </div>

          {/* Mobile-first responsive layout */}
          <div className="space-y-4 lg:space-y-0 lg:grid lg:grid-cols-4 lg:gap-6">
            {/* Categories - Mobile: Horizontal scroll, Desktop: Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-3 sm:p-4 lg:p-6 border border-white/20">
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-2 sm:mb-3 lg:mb-4">
                  <span className="hidden sm:inline">Molecule Categories</span>
                  <span className="sm:hidden">Categories</span>
                  <div className="text-sm sm:text-base lg:text-lg text-blue-200">
                    <span className="hidden sm:inline">فئات الجزيئات</span>
                    <span className="sm:hidden">الفئات</span>
                  </div>
                </h2>
                <p className="text-gray-300 mb-3 sm:mb-4 lg:mb-6 text-xs sm:text-sm lg:text-base hidden sm:block">
                  Select a category to explore
                  <div className="text-xs sm:text-sm text-gray-400">
                    اختر فئة للاستكشاف
                  </div>
                </p>
                
                {/* Mobile: Horizontal scrolling categories */}
                <div className="lg:hidden">
                  <div className="flex space-x-2 overflow-x-auto pb-2 scrollbar-hide">
                    {Object.entries(MOLECULE_CATEGORIES).map(([key, category]) => {
                      const IconComponent = category.icon;
                      return (
                        <button
                          key={key}
                          onClick={() => setSelectedCategory(key)}
                          className={`flex-shrink-0 p-2 sm:p-3 rounded-lg border-2 transition-all duration-200 flex flex-col items-center gap-1 min-w-[80px] sm:min-w-[100px] touch-manipulation ${
                            selectedCategory === key
                              ? 'border-blue-400 bg-blue-500/20 text-white'
                              : 'border-white/20 bg-white/5 text-gray-300 active:border-blue-300 active:bg-blue-500/10'
                          }`}
                        >
                          <div className={`p-1.5 sm:p-2 rounded-lg ${category.color}`}>
                            <IconComponent className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                          </div>
                          <div className="text-center">
                            <div className="font-semibold text-xs sm:text-sm leading-tight">
                              {category.title.split(' ')[0]}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Desktop: Vertical categories */}
                <div className="hidden lg:block space-y-3">
                  {Object.entries(MOLECULE_CATEGORIES).map(([key, category]) => {
                    const IconComponent = category.icon;
                    return (
                      <button
                        key={key}
                        onClick={() => setSelectedCategory(key)}
                        className={`w-full p-3 rounded-lg border-2 transition-all duration-200 flex items-center gap-3 ${
                          selectedCategory === key
                            ? 'border-blue-400 bg-blue-500/20 text-white'
                            : 'border-white/20 bg-white/5 text-gray-300 hover:border-blue-300 hover:bg-blue-500/10'
                        }`}
                      >
                        <div className={`p-2 rounded-lg ${category.color}`}>
                          <IconComponent className="w-5 h-5 text-white" />
                        </div>
                        <div className="text-left">
                          <div className="font-semibold">{category.title}</div>
                          <div className="text-sm opacity-75">{category.titleAr}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Molecules Grid - Mobile optimized */}
            <div className="lg:col-span-3">
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-3 sm:p-4 lg:p-6 border border-white/20">
                <div className="flex items-center justify-between mb-3 sm:mb-4 lg:mb-6">
                  <div>
                    <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-1">
                      {MOLECULE_CATEGORIES[selectedCategory]?.title || 'Molecules'}
                      <div className="text-sm sm:text-base lg:text-lg text-blue-200">
                        {MOLECULE_CATEGORIES[selectedCategory]?.titleAr || 'الجزيئات'}
                      </div>
                    </h2>
                    <p className="text-gray-300 text-xs sm:text-sm lg:text-base">
                      <span className="hidden sm:inline">Select a molecule to view its 3D structure</span>
                      <span className="sm:hidden">Tap to view 3D</span>
                      <div className="text-xs sm:text-sm text-gray-400">
                        <span className="hidden sm:inline">اختر جزيئًا لعرض هيكله ثلاثي الأبعاد</span>
                        <span className="sm:hidden">اضغط للعرض ثلاثي الأبعاد</span>
                      </div>
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-blue-300 font-bold text-sm sm:text-base">
                      {categoryMolecules.length}
                    </div>
                    <div className="text-gray-400 text-xs">
                      <span className="hidden sm:inline">molecules</span>
                      <span className="sm:hidden">items</span>
                      <div className="text-xs text-gray-500">
                        <span className="hidden sm:inline">جزيئات</span>
                        <span className="sm:hidden">عناصر</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Mobile-optimized grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                  {categoryMolecules.map((moleculeKey) => {
                    const molecule = MOLECULES[moleculeKey];
                    if (!molecule) return null;
                    
                    return (
                      <div
                        key={moleculeKey}
                        onClick={() => handleMoleculeSelect(moleculeKey)}
                        className="bg-white/10 backdrop-blur-sm rounded-lg p-3 sm:p-4 border border-white/20 hover:border-blue-400 active:border-blue-500 hover:bg-white/20 active:bg-white/25 transition-all duration-200 cursor-pointer group touch-manipulation"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="text-base sm:text-lg lg:text-xl font-bold text-white group-hover:text-blue-300 group-active:text-blue-200 leading-tight">
                              {molecule.name}
                              <div className="text-sm sm:text-base text-blue-200 font-normal">
                                {getArabicMoleculeName(molecule.name)}
                              </div>
                            </h3>
                          </div>
                          <div className="text-blue-300 font-mono text-sm sm:text-base lg:text-lg flex-shrink-0 ml-2">
                            {molecule.formula}
                          </div>
                        </div>
                        <p className="text-gray-300 text-xs sm:text-sm mb-2 sm:mb-3 line-clamp-2">
                          {molecule.description}
                          <div className="text-xs text-gray-400 mt-1">
                            {getArabicDescription(molecule.description)}
                          </div>
                        </p>
                        <div className="grid grid-cols-3 gap-2 text-xs text-gray-400">
                          <div className="text-center">
                            <div className="font-semibold text-white text-sm">{molecule.molarMass}</div>
                            <div>g/mol</div>
                            <div className="text-xs text-gray-500">جم/مول</div>
                          </div>
                          <div className="text-center">
                            <div className="font-semibold text-white text-sm">{molecule.atoms.length}</div>
                            <div>atoms</div>
                            <div className="text-xs text-gray-500">ذرات</div>
                          </div>
                          <div className="text-center">
                            <div className="font-semibold text-white text-sm">{molecule.bonds.length}</div>
                            <div>bonds</div>
                            <div className="text-xs text-gray-500">روابط</div>
                          </div>
                        </div>
                        
                        {/* Mobile tap indicator */}
                        <div className="mt-2 sm:mt-3 flex items-center justify-center">
                          <div className="bg-blue-500/20 text-blue-300 px-2 py-1 rounded-full text-xs flex items-center gap-1">
                            <span>👆</span>
                            <div>
                              <span className="hidden sm:inline">Tap to view</span>
                              <span className="sm:hidden">View</span>
                              <div className="text-xs text-blue-400">
                                <span className="hidden sm:inline">اضغط للعرض</span>
                                <span className="sm:hidden">عرض</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Empty state for mobile */}
                {categoryMolecules.length === 0 && (
                  <div className="text-center py-8 sm:py-12">
                    <Atom className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-500 mb-3 sm:mb-4" />
                    <p className="text-gray-400 text-sm sm:text-base">
                      No molecules in this category
                      <div className="text-xs sm:text-sm text-gray-500 mt-1">
                        لا توجد جزيئات في هذه الفئة
                      </div>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ViewerPage = () => {
    const molecule = MOLECULES[selectedMolecule];
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 p-2 sm:p-4 lg:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Mobile-optimized header */}
          <div className="flex items-center justify-between mb-3 sm:mb-4 lg:mb-6">
            <h1 className="text-lg sm:text-2xl lg:text-3xl font-bold text-white flex items-center gap-2 sm:gap-3">
              <Atom className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-blue-400" />
              <div>
                <span className="hidden sm:inline">OrynxChemistry</span>
                <span className="sm:hidden">OrynxChem</span>
                <div className="text-sm sm:text-lg lg:text-xl text-blue-200">
                  <span className="hidden sm:inline">أورينكس كيمياء</span>
                  <span className="sm:hidden">أورينكس</span>
                </div>
              </div>
            </h1>
            <button
              onClick={() => setCurrentPage('explorer')}
              className="bg-green-600 hover:bg-green-700 active:bg-green-800 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-lg flex items-center gap-1 sm:gap-2 transition-colors touch-manipulation text-sm sm:text-base"
            >
              <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
              <div>
                <span className="hidden sm:inline">Back</span>
                <span className="sm:hidden">←</span>
                <div className="text-xs sm:text-sm">
                  <span className="hidden sm:inline">رجوع</span>
                </div>
              </div>
            </button>
          </div>

          {molecule && (
            <div className="space-y-4 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-6">
              {/* 3D Viewer - Mobile first */}
              <div className="lg:col-span-2 order-1 lg:order-1">
                <div className="bg-white/10 backdrop-blur-md rounded-xl p-3 sm:p-4 lg:p-6 border border-white/20">
                  <div className="flex items-center justify-between mb-3 sm:mb-4">
                    <div>
                      <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                        {molecule.name}
                        <div className="text-sm sm:text-base lg:text-lg text-blue-200">
                          {getArabicMoleculeName(molecule.name)}
                        </div>
                      </h2>
                    </div>
                    <div className="text-blue-300 font-mono text-base sm:text-lg lg:text-xl">
                      {molecule.formula}
                    </div>
                  </div>
                  
                  {/* Mobile-optimized 3D canvas container */}
                  <div className="bg-black/20 rounded-lg p-2 sm:p-3 lg:p-4 flex justify-center relative">
                    <canvas
                      ref={canvasRef}
                      className="rounded-lg shadow-lg w-full h-auto max-w-full"
                      style={{ 
                        aspectRatio: '4/3',
                        maxHeight: '60vh',
                        touchAction: 'none'
                      }}
                    />
                    
                    {/* Mobile touch instructions overlay */}
                    <div className="absolute top-2 left-2 sm:top-3 sm:left-3 bg-black/50 backdrop-blur-sm rounded-lg p-2 text-white text-xs sm:text-sm lg:hidden">
                      <div className="flex items-center gap-1 mb-1">
                        <span>👆</span>
                        <div>
                          <span>Touch to rotate</span>
                          <div className="text-xs text-gray-300">لمس للدوران</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <span>🤏</span>
                        <div>
                          <span>Pinch to zoom</span>
                          <div className="text-xs text-gray-300">قرص للتكبير</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Loading indicator */}
                    {isLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg">
                        <div className="text-center">
                          <Atom className="w-8 h-8 sm:w-12 sm:h-12 mx-auto text-blue-400 animate-spin mb-2" />
                          <p className="text-white text-sm sm:text-base">
                            Loading 3D Model...
                            <div className="text-xs sm:text-sm text-gray-300 mt-1">
                              تحميل النموذج ثلاثي الأبعاد...
                            </div>
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Molecule Info - Mobile optimized */}
              <div className="lg:col-span-1 order-2 lg:order-2">
                <div className="bg-white/10 backdrop-blur-md rounded-xl p-3 sm:p-4 lg:p-6 border border-white/20">
                  <h3 className="text-base sm:text-lg lg:text-xl font-bold text-white mb-3 sm:mb-4">
                    <span className="hidden sm:inline">Molecule Information</span>
                    <span className="sm:hidden">Info</span>
                    <div className="text-sm sm:text-base text-blue-200">
                      <span className="hidden sm:inline">معلومات الجزيء</span>
                      <span className="sm:hidden">معلومات</span>
                    </div>
                  </h3>
                  
                  <div className="space-y-3 sm:space-y-4">
                    {/* Mobile-optimized info cards */}
                    <div className="bg-white/5 rounded-lg p-2 sm:p-3">
                      <label className="text-gray-300 text-xs sm:text-sm block mb-1">
                        Chemical Formula
                        <div className="text-xs text-gray-400">الصيغة الكيميائية</div>
                      </label>
                      <div className="text-xl sm:text-2xl font-mono text-blue-300">{molecule.formula}</div>
                    </div>
                    
                    <div className="bg-white/5 rounded-lg p-2 sm:p-3">
                      <label className="text-gray-300 text-xs sm:text-sm block mb-1">
                        Description
                        <div className="text-xs text-gray-400">الوصف</div>
                      </label>
                      <div className="text-white text-sm sm:text-base leading-relaxed">
                        {molecule.description}
                        <div className="text-xs sm:text-sm text-gray-300 mt-1">
                          {getArabicDescription(molecule.description)}
                        </div>
                      </div>
                    </div>
                    
                    {/* Mobile-optimized stats grid */}
                    <div className="grid grid-cols-2 gap-2 sm:gap-3">
                      <div className="bg-white/5 rounded-lg p-2 sm:p-3 text-center">
                        <label className="text-gray-300 text-xs block mb-1">
                          Molar Mass
                          <div className="text-xs text-gray-400">الكتلة المولية</div>
                        </label>
                        <div className="text-white font-bold text-sm sm:text-base">{molecule.molarMass}</div>
                        <div className="text-gray-400 text-xs">g/mol</div>
                        <div className="text-gray-500 text-xs">جم/مول</div>
                      </div>
                      
                      <div className="bg-white/5 rounded-lg p-2 sm:p-3 text-center">
                        <label className="text-gray-300 text-xs block mb-1">
                          Category
                          <div className="text-xs text-gray-400">الفئة</div>
                        </label>
                        <div className="text-white font-bold text-sm sm:text-base capitalize">
                          {MOLECULE_CATEGORIES[molecule.category]?.title.split(' ')[0] || 'Unknown'}
                          <div className="text-xs text-gray-300">
                            {MOLECULE_CATEGORIES[molecule.category]?.titleAr.split(' ')[0] || 'غير معروف'}
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-white/5 rounded-lg p-2 sm:p-3 text-center">
                        <label className="text-gray-300 text-xs block mb-1">
                          Atoms
                          <div className="text-xs text-gray-400">الذرات</div>
                        </label>
                        <div className="text-white font-bold text-lg sm:text-xl">{molecule.atoms.length}</div>
                      </div>
                      
                      <div className="bg-white/5 rounded-lg p-2 sm:p-3 text-center">
                        <label className="text-gray-300 text-xs block mb-1">
                          Bonds
                          <div className="text-xs text-gray-400">الروابط</div>
                        </label>
                        <div className="text-white font-bold text-lg sm:text-xl">{molecule.bonds.length}</div>
                      </div>
                    </div>
                    
                    {/* Element composition for mobile */}
                    <div className="bg-white/5 rounded-lg p-2 sm:p-3">
                      <label className="text-gray-300 text-xs sm:text-sm block mb-2">
                        Elements
                        <div className="text-xs text-gray-400">العناصر</div>
                      </label>
                      <div className="flex flex-wrap gap-1 sm:gap-2">
                        {[...new Set(molecule.atoms.map(atom => atom.element))].map(element => {
                          const count = molecule.atoms.filter(atom => atom.element === element).length;
                          const atomData = ATOMIC_DATA[element];
                          return (
                            <div key={element} className="flex items-center gap-1 bg-white/10 rounded-full px-2 py-1">
                              <div 
                                className="w-3 h-3 sm:w-4 sm:h-4 rounded-full border border-white/20"
                                style={{ backgroundColor: atomData?.color || '#888888' }}
                              ></div>
                              <span className="text-white text-xs sm:text-sm font-mono">
                                {element}
                                {count > 1 && <sub className="text-blue-300">{count}</sub>}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-center">
          <Atom className="w-16 h-16 mx-auto text-blue-400 animate-spin mb-4" />
          <p className="text-white text-xl">Loading 3D Molecule...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {currentPage === 'landing' && <LandingPage />}
      {currentPage === 'explorer' && <ExplorerPage />}
      {currentPage === 'viewer' && <ViewerPage />}
    </div>
  );
}

export default App;
